import SearchIcon from '@mui/icons-material/Search';
import { Grid, InputAdornment, InputLabel, Menu, MenuItem, MenuList, Stack, TextField, Typography } from '@mui/material';
import React, { forwardRef, useContext, useImperativeHandle, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { LanguageContext } from '../../../../../../libs/Context';
import TimePlanner from './Components/TimeZonePlanner';

import { IoMdArrowDropdown } from 'react-icons/io';
interface TimeZoneProps
{
	formDate: any;
	handleUpdateDate: (dt: any) => void;
	dataTimeZone: any;
	onTimezoneChange?: (timezone: string) => void;
}

const TimeZone = forwardRef((props: TimeZoneProps, ref) =>
{
	const { formDate, handleUpdateDate, dataTimeZone, onTimezoneChange } = props;
	const { language } = useContext(LanguageContext);
	const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	const timezones = moment.tz.names();
	const [ search, setSearch ] = useState<string>('');
	const [ selectedZone, setSelectedZone ] = useState<string>(dataTimeZone || (timeZone ?? 'Asia/Kolkata'));
	const [ open, setOpen ] = useState<boolean>(false);
	const [ anchorEls, setAnchorEls ] = useState<HTMLElement | null>(null);

	const handleClick = (event) =>
	{
		setAnchorEls(event.currentTarget);
	};

	const filteredTimezones = useMemo(() =>
	{
		return timezones.filter((tz) =>
			tz.toLowerCase().includes(search.toLowerCase()));
	}, [ timezones, search ]);

	const handleMenuItemClick = (item) =>
	{
		setSearch('');
		setSelectedZone(item);
		setAnchorEls(null);
		// Notify parent component about timezone change
		if (onTimezoneChange)
		{
			onTimezoneChange(item);
		}
	};

	// Keep the ref for backward compatibility, but also notify parent via callback
	useImperativeHandle(ref, () => (selectedZone), [ selectedZone ]);

	return (
		<Grid item xs={12} sm={6} lg={6}>
			<Stack sx={{ width: 'var(--percent100)' }} spacing={1}>
				<Stack direction={'row'} justifyContent={'space-between'}>
					<InputLabel sx={{ fontSize: 'var(--pixel14)', color: 'var(--black)' }}>{language?.timeZone}</InputLabel>
					<Typography fontSize='var(--pixel14)' px='var(--pixel8)' sx={{ textDecoration: 'underline', color: 'var(--primaryColor)', cursor: 'pointer' }} onClick={() => setOpen(true)}>
						{language?.planner}
					</Typography>
				</Stack>
				<TextField
					variant='outlined'
					type='text'
					size='small'
					fullWidth
					placeholder={language?.search_time_zone}
					onClick={(e) => handleClick(e)}
					value={selectedZone}
					InputProps={{
						endAdornment : (
							<InputAdornment position='start'>
								<IoMdArrowDropdown fontSize='var(--pixel20)'/>
							</InputAdornment>
						)
					}}
					sx={{
						cursor                     : 'pointer',
						'& .MuiOutlinedInput-root' : {
							cursor : 'pointer' // Apply cursor pointer to the root of the input
						},
						'& .MuiOutlinedInput-input' : {
							cursor : 'pointer' // Ensure the actual input element is clickable
						}
					}}
				/>
				<Menu
					id='location'
					anchorEl={anchorEls}
					open={Boolean(anchorEls)}
					onClose={() => setAnchorEls(null)}
					MenuListProps={{
						'aria-labelledby' : 'basic-button'
					}}
					sx={{ '& .MuiPaper-root' : { maxHeight : 'var(--font200)',
						// maxWidth: isMobileSM ? width : mobileSize
						width     : anchorEls ? `${anchorEls.getBoundingClientRect().width}px` : 'auto' // Dynamically match the width of the TextField

					}
					}}
				>
					<MenuList>
						<MenuItem>
							<TextField
								variant='outlined'
								type='search'
								size='small'
								sx={{ width: 400 }}
								value={search}
								onChange={(e) => setSearch(e.target.value.replace(/^\s+/, ''))}
								placeholder={language?.searchDot}
								InputProps={{
									endAdornment : (
										<InputAdornment position='end'>
											<SearchIcon />
										</InputAdornment>
									)
								}}
								id='search'
							/>
						</MenuItem>
						{filteredTimezones.map((tz) => (
							<MenuItem key={tz} value={tz}
								onClick={() => handleMenuItemClick(tz)}
							>
								({moment.tz(tz).format('Z')}) {tz}
							</MenuItem>))
						}
					</MenuList>
				</Menu>
			</Stack>
			{open &&
				<TimePlanner
					handleSubmit={handleUpdateDate}
					formDate={formDate}
					selectedZone={selectedZone}
					open={open}
					handleClose={() => setOpen(false)}
				/>
			}
		</Grid>
	);
});

TimeZone.displayName = 'TimeZone';
export default TimeZone;
